// 2026-07-21 - FormuLearning - Lot·배치 대면 API 호출 (이슈 #43)
// 2026-07-23 수정 - 이슈 #50(P1): Lot 조작 클라이언트 4종(생성·활성 전환·숨기기·대조약 저장).
//                   위치기반 rld_experiment 왕복을 대체하는 id기반 전용 경로(인터페이스 §2.10.3).
// 2026-08-30 수정 - 이슈 #58 S1(#64): Pull 호출 2종 **제거**(§13.7 폐기).
//                   Lot 제외 토글·optimization 대조약 후보 조회 신설(BR-LOT-14·BR-RND-08).
// 2026-08-31 수정 - 이슈 #58 S5(#68): `saveBatchComposition()` 신설 — 성분표 셀 편집의
//                   저장 경로(`M84-09`, 인터페이스 v1.8 §2.10.4).
// 2026-08-31 수정 - 이슈 #58 S5 2차(#68): `saveBatchResults()` 신설 — 결과 입력 패널의
//                   저장 경로(`M84-12`, 인터페이스 v1.9 §2.10.4).
// 2026-09-21 수정 - 이슈 #69(S6, M84-24): `getF2Analysis()` 신설 — 지정 대조약 기준 용출 비교
//                   분석(화면 전용, 저장하지 않는다).
// 2026-09-23 수정 - 이슈 #67 S4-4: `LotBatch` 폐기 예측 3필드 → `predicted` · `apiErrorDetail` 이 객체 detail
//                   (`{code, message}`)의 message 를 꺼낸다(인터페이스 v1.14 §2.6).
// 2026-09-24 수정 - 이슈 #75(G4): Lot 제외 폐지(`M901-18`) — `setLotExcluded`·`LotInfo` 제외 4필드 삭제.
// 2026-09-24 수정 - 이슈 #76(G5): `ReferenceLotCandidate.grid` — «계산 제외» 칩의 원천(기준 Lot 격자).
// 2026-09-26 수정 - 이슈 #95 단계 3-5: `uploadBatchResult` 삭제(`M84-75` 업로드 폐기).
// 2026-11-03 수정 - 이슈 #114: `LotBatch.ai_commentary_ko`(viewer 화면 내 라운드 전환용).
// 2026-11-03 수정 - 이슈 #102(D4): `saveBatchComposition` 값에 `null`(칸 비우기, User Define 초기 설계 배치만).
// 파일 위치: frontend/src/api/lot.ts
// 목적: 화면 통제 단위를 추천 회차(run_id)에서 배치(batch_code)로 전환한다.
//       포함/제외·액션 이력·배치 지정 (재)업로드는 모두 이 경로를 쓴다(BR-S4-L02~L04·L14~L17).

import { isAxiosError } from 'axios'
import client from './client'
import type { ApiResponse } from '../types/api'
import type { DissolutionProfile, PhProfile } from './step3'

// BR-S4-L03 제외 유형 4종. 제외 시 필수이며 복원하면 해제된다.
export const EXCLUDE_TYPES = ['experiment_error', 'outlier', 'condition_mismatch', 'other'] as const
export type ExcludeType = (typeof EXCLUDE_TYPES)[number]

export const EXCLUDE_TYPE_LABELS: Record<ExcludeType, string> = {
  experiment_error: '실험오류',
  outlier: '이상치',
  condition_mismatch: '조건불일치',
  other: '기타',
}

/** 활성 Lot의 시험 배치 1건. init·opti를 같은 형태로 다룬다. */
export interface LotBatch {
  id: string
  batch_code: string
  batch_type: 'init' | 'opti'
  seq: number | null
  /** 소속 라운드 UUID(#58 S1). reference 는 null이지만 이 표에는 시험약만 온다. */
  round_id: string | null
  /** 라운드 연번 — 구 정수 회차(run_id) 자리를 잇는 어댑터 값(인터페이스 §2.10.2). */
  round_no: number | null
  x_values: Record<string, number>
  recommended_x: Record<string, number>
  y_values: Record<string, number>
  dissolution_profiles: DissolutionProfile[]
  f2: number | null
  f2_by_solution: Record<string, number>
  f2_reason: string | null
  /** 목적함수별 예측(BR-OPT-14) — 폐기 3필드(predicted_mean·std·model_confidence) 대체(#67 S4-4). */
  predicted: Record<string, { mean: number; std: number } | string> | null
  /** 처방별 AI 해설(#114 — viewer 화면 내 라운드 전환용). init·대조약 배치는 null. */
  ai_commentary_ko?: string | null
  is_excluded: boolean
  exclude_type: ExcludeType | null
  exclude_reason: string | null
  /** 실험 결과(Y 실측 또는 용출 프로파일) 보유 여부. 재업로드 경고 판정에 쓴다. */
  has_result: boolean
  status: string
}

export interface BatchAction {
  id: string
  /** 배치가 삭제되면 null — 이력은 남고 batch_code로만 식별된다(이슈 #50, 260803). */
  batch_id: string | null
  batch_code: string
  action: 'upload' | 'reupload' | 'exclude' | 'restore' | 'dissolution_save' | 'composition_save'
  exclude_type: ExcludeType | null
  reason: string | null
  user_id: string | null
  meta: Record<string, unknown>
  created_at: string
}

export const ACTION_LABELS: Record<BatchAction['action'], string> = {
  upload: '업로드',
  reupload: '재업로드',
  exclude: '제외',
  restore: '복원',
  dissolution_save: '용출 저장',
  composition_save: '조성 저장',
}

/** Lot 목록 1건 — Step 4 Lot 선택기·배너 표시용(이슈 #50, 260723 피드백). */
export interface LotListItem {
  id: string
  lot_no: number
  label: string | null
  test_date: string | null
}

/** 활성 Lot 문맥 + 그 Lot의 시험 배치 전량. */
export interface LotBatchesResponse {
  active_lot_no: number
  active_lot_label: string
  total_lots: number
  /** 대조약 프로파일 보유 여부. false면 f2가 산출될 수 없다. */
  has_reference: boolean
  /** 표시 Lot 목록 — Step 4 선택기용. 전환은 setActiveLot(Step 3 탭과 동일 경로). */
  lots: LotListItem[]
  batches: LotBatch[]
}

export async function getLotBatches(projectId: string): Promise<LotBatchesResponse> {
  const { data } = await client.get<ApiResponse<LotBatchesResponse>>(`/lots/${projectId}/batches`)
  return data.data!
}

// ── Lot 조작 (이슈 #50 P1, 프로세스 §13.10 · 인터페이스 §2.10.3) ────────

/** Lot 1건 — 백엔드 LotSchema 대응. test_date가 측정일 표시 정본(BR-LOT-12). */
export interface LotInfo {
  id: string
  project_id: string
  lot_no: number
  label: string | null
  test_date: string | null
  grid: { time_points?: number[]; solutions?: string[] }
  is_deleted: boolean
  created_at: string
  updated_at: string
}

/**
 * Lot 표시명 — 사용자가 입력한 표기(`label`)가 있으면 **그것으로 대체**하고, 비어 있으면
 * `Lot {번호}`로 폴백한다(이슈 #60, M84-18 · 사용자 결정 260814).
 *
 * 종전에는 `label`을 저장만 하고 어디에도 쓰지 않아 "입력받고 안 쓰는" 상태였다.
 * 폴백을 두는 것은 규칙 완화가 아니라 결측 처리다 — `label`은 선택 입력이라
 * 공백일 때 식별자가 사라지면 Lot을 지목할 수 없다.
 *
 * 표시 규칙의 정본은 `BR-LOT-12`("번호 + 측정일")이며, 이 결정이 그 **개정 방향을
 * 확정**한다. 조항 개정 자체는 P4(정본 개정)에서 수행한다 — 변경등록부 `M84-18` 참조.
 * 측정일 병기 여부는 호출부가 정한다(탭·선택기는 병기, 배너는 미병기).
 */
export function lotDisplayName(label: string | null | undefined, lotNo: number | null | undefined): string {
  const t = (label ?? '').trim()
  return t !== '' ? t : `Lot ${lotNo ?? ''}`.trimEnd()
}

/** 새 Lot 생성(BR-LOT-08·09). 디폴트 격자·빈 대조약 배치는 백엔드가 부착한다. */
export async function createLot(projectId: string): Promise<LotInfo> {
  const { data } = await client.post<ApiResponse<LotInfo>>(`/lots/${projectId}/`)
  return data.data!
}

/** 활성 Lot 전환(BR-LOT-08). 전환 주체는 Step 3, Step 4는 표시만. */
export async function setActiveLot(projectId: string, lotId: string): Promise<{ active_lot_no: number }> {
  const { data } = await client.put<ApiResponse<{ active_lot_no: number }>>(
    `/lots/${projectId}/active`,
    { lot_id: lotId },
  )
  return data.data!
}

/** Lot 숨기기 = 소프트 삭제(BR-LOT-11). 데이터 보존, 목록에서만 감춘다. 마지막 Lot은 409. */
export async function hideLot(
  projectId: string,
  lotId: string,
): Promise<{ hidden_lot_no: number; active_lot_no: number }> {
  const { data } = await client.delete<ApiResponse<{ hidden_lot_no: number; active_lot_no: number }>>(
    `/lots/${projectId}/${lotId}`,
  )
  return data.data!
}

/** 대조약 프로파일 저장(BR-S3-L02). 격자 잠금(init·opti 존재) 시 서버가 409로 거부한다(BR-LOT-10). */
export async function saveReference(
  projectId: string,
  lotId: string,
  phProfiles: PhProfile[],
  testDate: string | null,
  label: string | null,
): Promise<LotInfo> {
  const { data } = await client.put<ApiResponse<LotInfo>>(
    `/lots/${projectId}/${lotId}/reference`,
    { ph_profiles: phProfiles, test_date: testDate, label },
  )
  return data.data!
}

// ── 대조약 후보 (이슈 #58 S1, BR-RND-08) ───────────────────────────────
// 2026-09-24(#75): Lot 제외 토글 `setLotExcluded` 삭제 — Lot 제외 축 폐지(`M901-18`).
// 2026-08-30: 구 Pull 2종(getPullCandidates·executePull)은 **폐기**됐다 — 끌어올
//   대상이던 init·opti 배치가 Lot 에 매이지 않고(§13.0), 대조약은 Lot 정체성과 1:1이라
//   끌어올 것이 없다. 격자 일치 판정만 아래 후보 조회로 승계됐다.

/** optimization 대상으로 고를 수 있는 Lot 1건(BR-RND-08). 불가 Lot 도 사유와 함께 온다. */
export interface ReferenceLotCandidate {
  id: string
  lot_no: number
  label: string | null
  test_date: string | null
  /** 활성 Lot 과 용출액·시점 집합이 모두 같은가. false면 비활성 표시한다. */
  selectable: boolean
  reason: string | null
  /** Lot 격자 `{time_points, solutions}`(#76) — 이번 run 에서 계산 제외할 시점·용출액을 고르는 칩의 원천. */
  grid?: { time_points: number[]; solutions: string[] }
}

export async function getReferenceLotCandidates(
  projectId: string,
): Promise<{ active_lot_id: string | null; candidates: ReferenceLotCandidate[] }> {
  const { data } = await client.get<
    ApiResponse<{ active_lot_id: string | null; candidates: ReferenceLotCandidate[] }>
  >(`/lots/${projectId}/reference-lot-candidates`)
  return data.data!
}

/**
 * 용출 비교 분석 결과(M84-24 · 이슈 #69) — **화면 분석 전용이며 저장하지 않는다.**
 * 기준 대조약을 생략하면 서버가 그 배치의 **run 시점 대조약**을 쓴다. 저장된 배치 f2 는
 * 언제나 run 시점 기준이다(BR-S4-L06). 대조약 프로파일이 함께 오므로 오버레이와 f2 가
 * 같은 원천을 본다. 판정(`similar_by_solution`)은 서버가 내린다 — 화면에서 다시 판정하지 않는다.
 */
export interface F2Analysis {
  batch_id: string
  batch_code: string
  /** 그 배치 라운드가 run 시점에 고른 대조약 Lot. 없으면(구 데이터) null. */
  run_reference_lot_id: string | null
  /** 이번 분석의 기준 대조약 Lot. */
  reference_lot_id: string | null
  reference_lot_no: number | null
  reference_label: string | null
  reference_test_date: string | null
  reference_profiles: PhProfile[]
  f2: number | null
  f2_by_solution: Record<string, number>
  similar_by_solution: Record<string, boolean>
  f2_reason: string | null
}

export async function getF2Analysis(
  projectId: string,
  batchId: string,
  referenceLotId: string | null,
): Promise<F2Analysis> {
  const { data } = await client.get<ApiResponse<F2Analysis>>(
    `/lots/${projectId}/batches/${batchId}/f2-analysis`,
    { params: referenceLotId ? { reference_lot_id: referenceLotId } : undefined },
  )
  return data.data!
}

/** 409(격자 잠금·마지막 Lot 등) 여부 판정. detail 메시지를 함께 꺼낸다. */
export function conflictDetail(e: unknown): string | null {
  if (isAxiosError(e) && e.response?.status === 409) {
    const detail = (e.response.data as { detail?: string } | undefined)?.detail
    return detail ?? '요청이 현재 상태와 충돌합니다.'
  }
  return null
}

/** 상태코드 무관 서버 detail 메시지 추출(422 업로드 정합 거부 등). 없으면 null. */
export function apiErrorDetail(e: unknown): string | null {
  if (isAxiosError(e)) {
    const detail = (e.response?.data as { detail?: unknown } | undefined)?.detail
    // 2026-09-23(#67 S4-4): `optimize`·`complete` 의 409·422 는 detail 이 객체 `{code, message}` 다
    //   (인터페이스 v1.14 §2.6) — 객체를 그대로 렌더하지 않도록 message 를 꺼낸다.
    if (detail && typeof detail === 'object' && 'message' in detail) {
      return String((detail as { message: unknown }).message)
    }
    return typeof detail === 'string' ? detail : null
  }
  return null
}

/** 배치 1건의 계산 포함/제외 전환. 제외 시 유형은 서버에서 필수 검증한다. */
export async function setBatchExcluded(
  projectId: string,
  batchId: string,
  isExcluded: boolean,
  excludeType?: ExcludeType,
  reason?: string,
): Promise<LotBatch> {
  const { data } = await client.put<ApiResponse<LotBatch>>(
    `/lots/${projectId}/batches/${batchId}/exclude`,
    { is_excluded: isExcluded, exclude_type: excludeType ?? null, reason: reason ?? null },
  )
  return data.data!
}

/**
 * 성분표 셀 편집 → 배치 조성 저장(`M84-09`, 인터페이스 v1.8 §2.10.4).
 *
 * 값은 **% 단위**다. 화면은 `mg/T`·`%` 두 칸을 보이지만 보내는 것은 `%` 하나뿐이다 —
 * mg 는 총중량에서 나오는 파생 표시라, 둘 다 저장하면 총중량이 바뀌는 순간 정본이 갈린다.
 * `null` 은 칸 비우기(키 제거)다 — User Define 초기 설계 배치만 받는다(#102, 그 밖은 422).
 */
export async function saveBatchComposition(
  projectId: string,
  batchId: string,
  actualX: Record<string, number | null>,
): Promise<LotBatch> {
  const { data } = await client.put<ApiResponse<LotBatch>>(
    `/lots/${projectId}/batches/${batchId}/composition`,
    { actual_x: actualX },
  )
  return data.data!
}

/**
 * 결과 입력 패널 → 배치 실측 결과 저장(`M84-12`, 인터페이스 v1.9 §2.10.4).
 *
 * 키는 Step 3 Y 변수 이름이다. **f2 는 보내지 않는다** — 용출 프로파일에서 산출되는
 * 시스템 계산값이라 서버가 422 로 되돌린다. `null` 은 **비우기**이며 0 과 구분된다.
 */
export async function saveBatchResults(
  projectId: string,
  batchId: string,
  yValues: Record<string, number | null>,
): Promise<LotBatch> {
  const { data } = await client.put<ApiResponse<LotBatch>>(
    `/lots/${projectId}/batches/${batchId}/results`,
    { y_values: yValues },
  )
  return data.data!
}

export async function getBatchActions(projectId: string, batchId: string): Promise<BatchAction[]> {
  const { data } = await client.get<ApiResponse<{ actions: BatchAction[] }>>(
    `/lots/${projectId}/batches/${batchId}/actions`,
  )
  return data.data!.actions
}

// 2026-08-03 - 이슈 #50: 프로젝트 전체 이력. 배치 단위 조회는 batch_id를 키로 쓰므로
// 삭제된 배치(batch_id=null)의 이력에 도달할 수 없다. 삭제분을 보려면 이 경로를 쓴다.
export async function getProjectActions(projectId: string): Promise<BatchAction[]> {
  const { data } = await client.get<ApiResponse<{ actions: BatchAction[] }>>(
    `/lots/${projectId}/actions`,
  )
  return data.data!.actions
}

